import { useEffect, useMemo, useState } from "react";
import { API, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { createKeyboardNav } from "@/lib/keyboardNav";

const PAY_MODES = [["cash", "Cash"], ["upi", "UPI"], ["card", "Card"], ["credit", "Credit"], ["phonepay", "PhonePay"], ["paytm", "Paytm"]];

export const openTxnPdf = async (tid) => {
  try {
    const res = await fetch(`${API}/transactions/${tid}/pdf`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("erp_token")}` }, credentials: "include",
    });
    if (!res.ok) throw new Error("PDF generation failed");
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  } catch (e) { toast.error(e.message || "PDF generation failed"); }
};

export const PaymentPanel = ({ open, title, grandTotal, saving, onCancel, onConfirm }) => {
  const [rows, setRows] = useState([{ mode: "cash", amount: "" }]);

  useEffect(() => {
    if (!open) return;
    setRows([{ mode: "cash", amount: "" }]);
    const t = setTimeout(() => document.querySelector('[data-testid="pay-mode-0"]')?.focus(), 120);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  const totalPaid = useMemo(() => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0), [rows]);
  const balance = Math.round((grandTotal - totalPaid) * 100) / 100;

  const setRow = (i, k, v) => setRows(rs => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const addRow = () => setRows(rs => [...rs, { mode: "cash", amount: "" }]);
  const delRow = (i) => setRows(rs => rs.filter((_, j) => j !== i));

  const handleEnter = createKeyboardNav({
    selector: '[data-testid^="pay-mode-"], input[data-testid^="pay-amount-"]',
    onLastField: (c) => c.querySelector('[data-testid="pay-save-btn"]')?.focus(),
  });

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50" data-testid="payment-panel-wrap">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} data-testid="payment-panel-backdrop" />
      <div className="absolute inset-y-0 right-0 w-full sm:w-1/2 bg-card border-l border-border p-6 overflow-y-auto shadow-2xl"
        data-testid="payment-panel" onKeyDownCapture={handleEnter}>
        <div className="flex items-center justify-between border-b border-border pb-3">
          <h2 className="font-display uppercase text-lg">Payment — {title}</h2>
          <Button type="button" data-testid="pay-cancel-btn" variant="ghost" onClick={onCancel} className="rounded-sm text-xs uppercase tracking-wider text-muted-foreground"><X className="w-3 h-3 mr-1"/>Cancel</Button>
        </div>

        <div className="flex justify-between items-baseline border-b border-border py-4">
          <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Grand Total</span>
          <span className="font-display font-black text-3xl text-primary" data-testid="pay-grand-total">{inr(grandTotal)}</span>
        </div>

        <div className="mt-4 space-y-2">
          <div className="grid grid-cols-[1fr_130px_36px] gap-2 text-[10px] uppercase tracking-[0.15em] text-muted-foreground px-1">
            <div>Payment Mode</div><div className="text-right">Amount</div><div></div>
          </div>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_130px_36px] gap-2 items-center" data-testid={`pay-row-${i}`} data-kb-row={i}>
              <Select value={r.mode} onValueChange={(v) => setRow(i, "mode", v)}>
                <SelectTrigger data-testid={`pay-mode-${i}`} className="rounded-sm bg-secondary border-border"><SelectValue/></SelectTrigger>
                <SelectContent>{PAY_MODES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid={`pay-amount-${i}`} type="number" min="0" value={r.amount} onChange={(e) => setRow(i, "amount", e.target.value)} className="rounded-sm bg-secondary border-border h-9 text-right"/>
              <Button type="button" size="sm" variant="ghost" data-testid={`pay-del-${i}`} onClick={() => delRow(i)} disabled={rows.length === 1} className="h-9 w-9 p-0 text-destructive"><Trash2 className="w-4 h-4"/></Button>
            </div>
          ))}
          <Button type="button" data-testid="pay-add-mode" size="sm" variant="outline" onClick={addRow} className="rounded-sm text-xs uppercase tracking-wider"><Plus className="w-3 h-3 mr-1"/>Add Payment Mode</Button>
        </div>

        <div className="mt-6 space-y-2 text-sm border-t border-border pt-3">
          <div className="flex justify-between"><span className="text-muted-foreground">Total Paid</span><span className="font-mono text-green-500" data-testid="pay-total-paid">{inr(totalPaid)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Balance Due</span><span className={`font-mono ${balance > 0 ? "text-yellow-500" : "text-green-500"}`} data-testid="pay-balance-due">{inr(balance)}</span></div>
        </div>

        <Button data-testid="pay-save-btn" onClick={() => onConfirm(rows.filter(r => Number(r.amount) > 0))} disabled={saving}
          className="w-full mt-6 rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em]">
          {saving ? "Saving…" : "Save & Generate Invoice"}
        </Button>
      </div>
    </div>
  );
};
